import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import type { AfricanKeyboardProps, KeyboardLayer, KeyboardMode } from './keyboard.types';
import { resolveLayout, isFrenchLanguage } from './keyboard.layouts';
import { getVariants, hasVariants } from './keyboard.variants';
import { KEYBOARD_THEME_CSS } from './keyboard.theme';
import { KeyboardToolbar } from './KeyboardToolbar';
import { CharacterVariantsPopup } from './CharacterVariantsPopup';
import { KeyboardHelpModal } from './KeyboardHelpModal';
import { useApp } from '@/hooks/use-app-context';

const STORAGE_KEY_WIDTH = 'yodi-kb-width';
const STORAGE_KEY_MODE = 'yodi-kb-mode';
const MIN_WIDTH = 500;
const MAX_WIDTH_PCT = 0.96;
const LONG_PRESS_DELAY = 500; // ms

function loadWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_WIDTH);
    if (stored) return Number(stored);
  } catch {}
  return Math.min(1100, window.innerWidth * MAX_WIDTH_PCT);
}

function loadMode(): KeyboardMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MODE);
    if (stored === 'compact' || stored === 'extended') return stored;
  } catch {}
  return 'compact';
}

/**
 * NFC = Canonical Composition. Converts decomposed sequences (base + combining mark)
 * back to their precomposed single-codepoint form (e.g. "o" + U+0303 → "õ").
 */
function nfc(s: string): string {
  return s.normalize('NFC');
}

/**
 * Premium floating African keyboard with:
 * - Long-press character variants
 * - Fixed punctuation row
 * - Compact/Extended mode toggle
 * - African layout as default
 * - All African language characters
 * - Responsive and modular design
 */
export function FloatingAfricanKeyboard({
  language,
  value,
  onChange,
  onClose,
}: AfricanKeyboardProps) {
  const { t } = useApp();
  const [keyboardRef, setKeyboardRef] = useState<any>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Default to 'african' layer unless language is French
  const defaultLayer: KeyboardLayer = isFrenchLanguage(language) ? 'default' : 'african';
  const [layer, setLayer] = useState<KeyboardLayer>(defaultLayer);

  const [mode, setMode] = useState<KeyboardMode>(loadMode);
  const [kbWidth, setKbWidth] = useState<number>(loadWidth);
  const constraintsRef = useRef<HTMLDivElement>(null);

  // Long-press state
  const [variantsPopup, setVariantsPopup] = useState<{
    variants: string[];
    baseChar: string;
    buttonRect: DOMRect;
  } | null>(null);

  const longPressTimer = useRef<number | null>(null);
  const longPressButton = useRef<string | null>(null);

  const layout = resolveLayout(language);

  // Sync keyboard input with value prop
  useEffect(() => {
    keyboardRef?.setInput(value);
  }, [value, keyboardRef]);

  // Persist width
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_WIDTH, String(kbWidth));
    } catch {}
  }, [kbWidth]);

  // Persist mode
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MODE, mode);
    } catch {}
  }, [mode]);

  // Get current rows based on layer and mode
  const getCurrentRows = useCallback((): string[] => {
    const rows = layout[layer] ?? layout.african;

    // In extended mode, show all rows
    if (mode === 'extended') {
      return rows;
    }

    // In compact mode, limit rows to keep it smaller
    if (layer === 'african' || layer === 'africanUpper') {
      // Show first 3 rows + controls in compact mode for african
      return rows.slice(0, Math.min(4, rows.length));
    }

    return rows;
  }, [layer, mode, layout]);

  const currentRows = getCurrentRows();
  const layoutMap: Record<string, string[]> = { default: currentRows };
  const layoutKey = `${layer}-${mode}-${currentRows.length}`;
  const isShiftActive = layer === 'uppercase' || layer === 'africanUpper';

  // Handle shift toggle
  const toggleShift = useCallback(() => {
    setLayer((currentLayer) => {
      if (currentLayer === 'default') return 'uppercase';
      if (currentLayer === 'uppercase') return 'default';
      if (currentLayer === 'african') return 'africanUpper';
      if (currentLayer === 'africanUpper') return 'african';
      return currentLayer;
    });
  }, []);

  // Handle mode toggle
  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'compact' ? 'extended' : 'compact'));
  }, []);

  // Handle key press (shift, etc.)
  const handleKeyPress = useCallback(
    (button: string) => {
      if (button === '{shift}') {
        toggleShift();
      }
    },
    [toggleShift]
  );

  // Handle input change
  const handleChange = useCallback(
    (newInput: string) => {
      onChange(nfc(newInput));
    },
    [onChange]
  );

  // Handle layer change
  const handleLayerChange = useCallback((newLayer: KeyboardLayer) => {
    setLayer(newLayer);
  }, []);

  // Handle long press start
  const handleButtonMouseDown = useCallback((button: string, event: React.MouseEvent) => {
    // Check if button has variants
    if (!hasVariants(button)) return;

    longPressButton.current = button;
    const buttonElement = event.currentTarget as HTMLElement;

    longPressTimer.current = window.setTimeout(() => {
      const variants = getVariants(button);
      if (variants.length > 0) {
        const rect = buttonElement.getBoundingClientRect();
        setVariantsPopup({
          variants,
          baseChar: button,
          buttonRect: rect,
        });
      }
    }, LONG_PRESS_DELAY);
  }, []);

  // Handle long press end
  const handleButtonMouseUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressButton.current = null;
  }, []);

  // Handle variant selection from popup
  const handleVariantSelect = useCallback(
    (variant: string) => {
      const currentInput = keyboardRef?.getInput() || value;
      keyboardRef?.setInput(currentInput + variant);
      onChange(nfc(currentInput + variant));
      setVariantsPopup(null);
    },
    [keyboardRef, value, onChange]
  );

  // Handle resize
  const handleResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = kbWidth;
      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        const newW = Math.max(MIN_WIDTH, Math.min(startW + delta, window.innerWidth * MAX_WIDTH_PCT));
        setKbWidth(newW);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [kbWidth]
  );

  // Add event listeners to keyboard buttons for long press
  useEffect(() => {
    if (!keyboardRef) return;

    const keyboardElement = keyboardRef.keyboardDomNode;
    if (!keyboardElement) return;

    const buttons = keyboardElement.querySelectorAll('.hg-button');

    const handleMouseDown = (e: Event) => {
      const target = e.target as HTMLElement;
      const button = target.getAttribute('data-skbtn');
      if (button && hasVariants(button)) {
        handleButtonMouseDown(button, e as any);
      }
    };

    const handleMouseUp = () => {
      handleButtonMouseUp();
    };

    buttons.forEach((btn) => {
      btn.addEventListener('mousedown', handleMouseDown);
      btn.addEventListener('mouseup', handleMouseUp);
      btn.addEventListener('mouseleave', handleMouseUp);
    });

    return () => {
      buttons.forEach((btn) => {
        btn.removeEventListener('mousedown', handleMouseDown);
        btn.removeEventListener('mouseup', handleMouseUp);
        btn.removeEventListener('mouseleave', handleMouseUp);
      });
    };
  }, [keyboardRef, handleButtonMouseDown, handleButtonMouseUp]);

  return (
    <>
      <div ref={constraintsRef} className="fixed inset-0 z-50 pointer-events-none" />

      <motion.div
        drag
        dragConstraints={constraintsRef}
        dragElastic={0}
        dragMomentum={false}
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.95 }}
        transition={{ type: 'spring', damping: 28, stiffness: 350 }}
        className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 pointer-events-auto"
        style={{ width: kbWidth, maxWidth: `${MAX_WIDTH_PCT * 100}vw`, touchAction: 'none' }}
      >
        <div className="relative rounded-2xl border border-border/40 bg-[#0e1118]/95 shadow-[0_-8px_60px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.03)_inset] backdrop-blur-3xl">
          {/* Top glow */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/30 to-transparent" />

          {/* Toolbar */}
          <div className="relative">
            <KeyboardToolbar
              language={language}
              layer={layer}
              mode={mode}
              onLayerChange={handleLayerChange}
              onModeToggle={toggleMode}
              onHelpClick={() => setShowHelpModal(true)}
              onClose={onClose}
              t={t}
            />
          </div>

          {/* Keyboard */}
          <div className="relative p-3 pt-2 overflow-x-auto">
            <Keyboard
              key={layoutKey}
              keyboardRef={(r: any) => setKeyboardRef(r)}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              input={value}
              display={{
                '{bksp}': '⌫',
                '{space}': 'Espace',
                '{shift}': '⇧',
              }}
              buttonTheme={isShiftActive ? [{ class: 'yodi-shift-active', buttons: '{shift}' }] : []}
              layout={layoutMap}
              theme="hg-theme-default yodi-kb-theme"
            />
          </div>

          {/* Fixed punctuation row - always visible */}
          <div className="relative border-t border-white/[0.08] bg-white/[0.018] px-3 py-2">
            <div className="flex items-center justify-center gap-1">
              {['.', ',', '?', '!', "'", ':', ';', '-', '(', ')'].map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => {
                    const currentInput = keyboardRef?.getInput() || value;
                    keyboardRef?.setInput(currentInput + char);
                    onChange(nfc(currentInput + char));
                  }}
                  className="flex h-9 min-w-[2.5rem] items-center justify-center rounded-lg border border-white/[0.09] bg-white/[0.035] px-2 text-sm font-medium text-white/78 transition-all duration-150 hover:border-brand/35 hover:bg-brand/[0.10] hover:text-brand active:scale-95"
                >
                  {char}
                </button>
              ))}
            </div>
            <div className="mt-1 text-center text-[9px] text-white/45">
              {t.keyboard?.quickHint || 'Quick punctuation • Long-press keys for variants'}
            </div>
          </div>

          {/* Resize handle */}
          <div
            onPointerDown={handleResize}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-10 cursor-col-resize rounded-l-md bg-white/12 hover:bg-brand/35 transition-colors"
          />
        </div>

        <style>{KEYBOARD_THEME_CSS}</style>
      </motion.div>

      {/* Character variants popup */}
      {variantsPopup && (
        <CharacterVariantsPopup
          variants={variantsPopup.variants}
          baseChar={variantsPopup.baseChar}
          buttonRect={variantsPopup.buttonRect}
          onSelect={handleVariantSelect}
          onClose={() => setVariantsPopup(null)}
        />
      )}

      {/* Help modal */}
      <KeyboardHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        language={language}
        t={t}
      />
    </>
  );
}

